/**
 * ProposalDispatch — Daily power dispatch chart with day-selector pills
 *
 * Shows a stacked area chart (PowerGraph) of hourly dispatch for a scenario.
 * User can toggle between Peak Summer, Peak Winter, and Typical Day views.
 */

import React, { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { vortxColors, vortxTypography, PowerGraph } from '@vortx/shared-ui';
import { reoptToDailySeries } from '../../reoptTransform';

const pills = [
  { key: 'summer', label: 'Peak Summer' },
  { key: 'winter', label: 'Peak Winter' },
  { key: 'typical', label: 'Typical Day' },
];

export default function ProposalDispatch({ scenario }) {
  const results = scenario?.last_run_results || scenario?.reopt_results;
  const [selectedDay, setSelectedDay] = useState('summer');

  // Extract load series from outputs
  const loadSeries = useMemo(() => {
    const outputs = results?.outputs || {};
    return outputs?.Scenario?.Site?.ElectricLoad?.load_series_kw
      || outputs?.ElectricLoad?.load_series_kw
      || [];
  }, [results]);

  // Calculate day indices for peak summer, peak winter, and typical day
  const dayIndices = useMemo(() => {
    if (!loadSeries || loadSeries.length === 0) return null;

    const hoursPerDay = 24;
    const numDays = Math.floor(loadSeries.length / hoursPerDay);
    if (numDays === 0) return null;

    // Sum daily loads
    const dailyTotals = [];
    for (let d = 0; d < numDays; d++) {
      let total = 0;
      for (let h = 0; h < hoursPerDay; h++) {
        total += loadSeries[d * hoursPerDay + h] || 0;
      }
      dailyTotals.push({ day: d, total });
    }

    // Peak Summer: highest daily load in Jun-Aug (days 152-243)
    const summerDays = dailyTotals.filter(d => d.day >= 152 && d.day <= 243);
    const peakSummer = summerDays.length > 0
      ? summerDays.reduce((max, d) => d.total > max.total ? d : max, summerDays[0])
      : dailyTotals[0];

    // Peak Winter: highest daily load in Dec-Feb (days 0-59, 335-365)
    const winterDays = dailyTotals.filter(d => (d.day >= 0 && d.day <= 59) || (d.day >= 335 && d.day < numDays));
    const peakWinter = winterDays.length > 0
      ? winterDays.reduce((max, d) => d.total > max.total ? d : max, winterDays[0])
      : dailyTotals[0];

    // Typical Day: day with median daily load
    const sorted = [...dailyTotals].sort((a, b) => a.total - b.total);
    const medianIndex = Math.floor(sorted.length / 2);
    const typicalDay = sorted[medianIndex];

    return {
      summer: peakSummer.day,
      winter: peakWinter.day,
      typical: typicalDay.day,
    };
  }, [loadSeries]);

  // Early return if no usable data
  if (!results?.outputs || !loadSeries || loadSeries.length === 0 || !dayIndices) {
    return null;
  }

  const dayIndex = dayIndices[selectedDay] ?? dayIndices.summer;
  const { data: dispatchData, isOffGrid } = reoptToDailySeries(results, dayIndex);

  return (
    <Box>
      {/* Section header with title + pills */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="overline" sx={{ color: vortxColors.stone, fontWeight: 600, letterSpacing: '0.1em' }}>
          Daily Dispatch
        </Typography>
        {/* Day selector pills */}
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {pills.map(pill => (
            <Box
              key={pill.key}
              onClick={() => setSelectedDay(pill.key)}
              sx={{
                px: 1.5, py: 0.5, borderRadius: 1.5, cursor: 'pointer',
                border: '1px solid',
                borderColor: selectedDay === pill.key ? vortxColors.gold : 'rgba(255,255,255,0.12)',
                bgcolor: selectedDay === pill.key ? `${vortxColors.gold}15` : 'transparent',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: vortxColors.gold },
              }}
            >
              <Typography variant="body2" sx={{
                fontWeight: selectedDay === pill.key ? 600 : 400,
                color: selectedDay === pill.key ? vortxColors.gold : vortxColors.stone,
                fontFamily: vortxTypography.body, fontSize: '0.75rem',
              }}>
                {pill.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* PowerGraph chart */}
      <Box sx={{ borderRadius: 2, border: '1px solid rgba(255,255,255,0.08)', bgcolor: 'rgba(255,255,255,0.02)', p: 1, pb: 2 }}>
        <PowerGraph data={dispatchData} isOffGrid={isOffGrid} isDark={true} showControls={false} legendPosition="bottom" />
      </Box>
    </Box>
  );
}
